import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCertList, setCertCodeSubscribedList, setCertCodeInFocus } from "../../redux/slices/practiceSlice";
import { setIsExamInProgress, setLatestExamType } from "../../redux/slices/examSlice";
import { login, primaryRole } from "../../redux/slices/userSlice";
import TextField from "../../components/customMui/TextField";
import LoadingButton from "../../components/customMui/LoadingButton";
import Alert from "../../components/customMui/Alert";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import pagePath from "../../util/constants/pagePath";
import Turnstile from 'react-turnstile';
import "./login.css";
import Footer from "../../components/footer/Footer";


const Login = () => {
    const [submitLoading, setSubmitLoading] = useState(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [captchaSiteKey, setCaptchaSiteKey] = useState(null);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [countdown, setCountdown] = useState(0); // Using countdown for transparency
    const [captchaToken, setCaptchaToken] = useState(""); 
    const errorClearTimeoutRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchCaptchaSiteKey = async () => {
            try {
                const response = await standardBizApiClient.get_captcha_site_key();

                // Ensure you're accessing the correct site key
                const siteKey = response?.captcha_site_key;

                if (siteKey && typeof siteKey === 'string') {
                    setCaptchaSiteKey(siteKey); // Correctly setting the site key
                }
            } catch (error) {
                setErrorMessage("Error fetching CAPTCHA site key.");
            }
        };

        fetchCaptchaSiteKey();

        // Restore failed attempts and lockout state from localStorage
        const storedAttempts = localStorage.getItem("loginFailedAttempts");
        const storedExpiry = localStorage.getItem("loginLockoutExpiry");

        if (storedAttempts) {
            setFailedAttempts(parseInt(storedAttempts, 10));
        }

        if (storedExpiry) {
            const remaining = Math.ceil((parseInt(storedExpiry, 10) - Date.now()) / 1000);
            if (remaining > 0) {
                setCountdown(remaining);
            } else {
                localStorage.removeItem("loginLockoutExpiry");
            }
        }

        const handleStorageChange = (e) => {
            if (e.key === "loginLockoutExpiry") {
                if (e.newValue) {
                    const remaining = Math.ceil((parseInt(e.newValue, 10) - Date.now()) / 1000);
                    if (remaining > 0) setCountdown(remaining);
                } else {
                    setCountdown(0);
                    setErrorMessage(null);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            setErrorMessage(`Too many login attempts. Please wait ${formatTimer(countdown)} and try again.`);
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        setErrorMessage(null);
                        localStorage.removeItem("loginLockoutExpiry");
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const encodeEmailAddress = (email) => {
        // Apply custom encoding rules
        return email.replace(/@/g, "%40").replace(/\./g, "..");
    };

    const formatTimer = (totalSeconds) => {
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");
        return `${minutes}:${seconds}`;
    };

    const getLimitSecondsFromError = (errorObj) => {
        const kvdetail = errorObj?.kvdetail || [];
        const limitItem = kvdetail.find((item) => item.key === "limit_seconds_remaining" || item.key === "wait_seconds");
        return Number(limitItem?.value || 0);
    };

    const startLockout = (seconds) => {
        setCountdown(seconds);
        localStorage.setItem("loginLockoutExpiry", (Date.now() + seconds * 1000).toString());
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (countdown > 0) {
            setErrorMessage(`Too many login attempts. Please wait ${formatTimer(countdown)} and try again.`);
            return;
        }

        setErrorMessage(null);
        setSubmitLoading(true);

        if (captchaSiteKey && !captchaToken) {
            setErrorMessage('Please complete the CAPTCHA.');
            setSubmitLoading(false);
            return;
        }

        try {
            const encodedEmailAddress = encodeEmailAddress(emailAddress);
            const loginRes = await standardBizApiClient.account_login(
                encodedEmailAddress,
                password,
                captchaToken
            );

            if (loginRes?.is_error) {
                const newFailedAttempts = failedAttempts + 1;
                setFailedAttempts(newFailedAttempts);
                localStorage.setItem("loginFailedAttempts", newFailedAttempts.toString());

                if (newFailedAttempts === 6) {
                    startLockout(60);
                } else if (newFailedAttempts >= 9) {
                    startLockout(600);
                } else {
                    setErrorMessage('Error while logging in, please check your email and password.');
                }
                setSubmitLoading(false);
                return;
            }

            const { user_id, certs, primary_role, exam_meta, email_address } = loginRes;
            const { cert_code_in_focus, cert_code_subscribed_list, cert_list } = certs;
            const { exam_type } = exam_meta;
            const newIsExamInProgress = Boolean(exam_type);

            // Clear state on success
            setFailedAttempts(0);
            setCountdown(0);
            localStorage.removeItem("loginFailedAttempts");
            localStorage.removeItem("loginLockoutExpiry");

            dispatch(setCertCodeInFocus(cert_code_in_focus));
            dispatch(setCertCodeSubscribedList(cert_code_subscribed_list));
            dispatch(setCertList(cert_list));

            dispatch(setLatestExamType(exam_type));
            dispatch(setIsExamInProgress(newIsExamInProgress));
            dispatch(login({ userId: user_id, email: email_address }));
            dispatch(primaryRole(primary_role));
            setCountdown(0);

            // The cert code can come from three places:
            // 1. login response from backend (cross-device)
            // 2. ?cert= in the URL (verification-link fallback)
            // 3. localStorage (same-device fallback)
            const certFromUrl = new URLSearchParams(location.search).get("cert");
            const certFromStorage = localStorage.getItem("selected_cert_code");
            const selectedCert = loginRes?.selected_cert_code || certFromUrl || certFromStorage;

            if (!cert_code_subscribed_list || cert_code_subscribed_list.length === 0) {
                if (selectedCert) {
                    navigate(`${pagePath.practice_no_subscribed_certs}?cert=${selectedCert}`);
                } else {
                    navigate(pagePath.practice_no_subscribed_certs);
                }
            } else if (newIsExamInProgress) {
                navigate(pagePath.practice);
            } else {
                navigate(pagePath.dashboard);
            }

            localStorage.removeItem("selected_cert_code");
        } catch (error) {
            setErrorMessage('An unexpected error occurred. Please try again.');
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <div className="login">
                <h2 className="font-header-light">Login</h2>
                <form className="login-form" onSubmit={handleLogin}>
                    {errorMessage ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                    <TextField
                        required
                        disabled={submitLoading || countdown > 0}
                        value={emailAddress}
                        onChange={e => setEmailAddress(e.target.value)}
                        id="login-email-address"
                        name="email"
                        type="email"
                        label="Email Address"
                        autoComplete="username"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        required
                        disabled={submitLoading || countdown > 0}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        id="login-password"
                        name="password"
                        type="password"
                        label="Password"
                        autoComplete="current-password"
                        InputLabelProps={{ shrink: true }}
                    />

                    {/*
                    If a captcha site key is present
                    Then, display the Turnstile component.
                    Otherwise, display a message indicating that the captcha has been bypassed.
                 */}
                    {captchaSiteKey ? (
                        <Turnstile
                            sitekey={captchaSiteKey}
                            onVerify={token => setCaptchaToken(token)}
                        />
                    ) : <span>(Captcha Bypassed)</span>}

                    <LoadingButton customProps={{
                        type: "submit",
                        loading: submitLoading,
                        disabled: countdown > 0
                    }}>
                        Login
                    </LoadingButton>
                    <p style={{ textAlign: "center" }}>Forgot your password? Reset it <Link to={pagePath.reset_password_initiate}>here</Link></p>
                    <div className="login-divider"><div className="line"></div><div>or</div> <div className="line"></div></div>
                    <p style={{ textAlign: "center" }}>Don't have an account? <Link to={pagePath.signup}>Sign up</Link></p>
                </form>
            </div>
            <Footer />
        </>
    );
};

export default Login;
